import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../components/Modal";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import { createKassaTransaction } from "../../services/kassa/kassaServices";
import saveIcon from "../../assets/save_icon.png";
import closeIcon from "../../assets/close_icon.png";
import { formatDate } from "../../utils/toDate";
import { getPaymentCode } from "../../services/PaymentCodeService";
import TextDiv from "../../components/TextDiv";
import CustomInputWithUnit from "../../components/CustomInputWithUnit";
import Loader, { LOADER_SIZES } from "../../components/Loader";

const KassaDepositModal = ({ kassaId, handleToggleModal, refetchData }) => {
  const { t } = useTranslation("general");

  const [formData, setFormData] = useState({
    amount: "",
    date: new Date(),
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState();
  const [transactionCode, setTransactionCode] = useState("");

  const validateForm = () => {
    let newErrors = "";

    if (!formData.amount || formData.amount <= 0) {
      newErrors = t("amountRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const getTransCode = async () => {
    try {
      const response = await getPaymentCode();
      if (response.status == 200) {
        setTransactionCode(response?.data.code);
        return response?.data.code;
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);

    try {
      const transactionData = {
        kassaId: kassaId,
        amount: parseFloat(formData?.amount),
        type: "DIRECTDEPOSIT",
        date: formData.date,
        notes: formData.notes || undefined,
        receiptNumber: transactionCode,
      };

      const response = await createKassaTransaction(transactionData);

      if (response.status === 201) {
        // Show success message if you have a toast system
        console.log("Deposit transaction created successfully");
        handleToggleModal();
        refetchData();
      }
    } catch (error) {
      console.error("Error creating deposit transaction:", error);
      // Show error message if you have a toast system
      // You can set a general error state here if needed
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    handleToggleModal();
  };

  useEffect(() => {
    getTransCode();
  }, []);

  return (
    <Modal
      modalSize="c-modal--small"
      title={t("deposit")}
      onClose={handleToggleModal}
      footer={
        <>
          {loading && <Loader size={LOADER_SIZES.MEDIUM} />}
          <div className="o-row o-row--horizontal-align_end o-row--gap-size_400">
            <CustomButton
              variant="blank"
              title={t("cancel")}
              onClick={handleCancel}
              icon={closeIcon}
              iconSize={14}
              disabled={loading}
            />
            <CustomButton
              title={t("save")}
              onClick={handleSubmit}
              disabled={loading}
              icon={saveIcon}
              iconSize={14}
            />
          </div>
        </>
      }
      error={errors}
    >
      <div className="o-row">
        <p className="t-bold_semi-bold u-width-specific_100 u-margin_0">
          {t("receiptNumber")}:
        </p>
        <label className="t-bold_semi-bold u-margin_0">{transactionCode}</label>
      </div>

      <div className="o-column o-column--gap-size_300">
        <div className="o-column o-column--gap-size_200">
          <div className="o-row o-row--vertical-align_center">
            <label className="t-bold_semi-bold u-width-specific_100 u-margin_0">
              {t("amount")}:
            </label>
            <CustomInputWithUnit
              name="amount"
              type="moneyV2"
              value={formData.amount}
              onChange={handleInputChange}
              unit={"IQD"}
              thick
              flex
            />
          </div>

          <div className="o-row o-row--vertical-align_center">
            <label className="t-bold_semi-bold u-width-specific_100 u-margin_0">
              {t("Date")}:
            </label>
            <CustomInput
              name="date"
              type="date"
              value={formatDate(formData.date)}
              onChange={handleInputChange}
              thick
              flex
            />
          </div>

          <div className="o-column o-column--gap-size_200">
            <label className="t-bold_semi-bold">{t("notes")}:</label>
            <CustomInput
              name="notes"
              type="textarea"
              value={formData.notes}
              onChange={handleInputChange}
              thick
              flex
              rows={3}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default KassaDepositModal;
